import { Injectable, computed, inject, signal } from '@angular/core';
import { finalize } from 'rxjs';
import { ClientService } from './client.service';

export type UserNotification = {
  id: number;
  title: string;
  message: string;
  type: string;
  status: 'unread' | 'read';
  createdAt: string;
};

@Injectable()
export class UserNotificationService {
  private readonly client = inject(ClientService);
  readonly notifications = signal<UserNotification[]>([]);
  readonly loading = signal(false);
  readonly failed = signal(false);
  readonly unreadCount = computed(
    () =>
      this.notifications().filter((item) => item.status === 'unread').length,
  );

  load(): void {
    if (this.loading()) return;
    this.loading.set(true);
    this.client
      .get<unknown>('user-notification')
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          const body = this.record(response);
          const rows = Array.isArray(response)
            ? response
            : (body['data'] ?? body['Data']);
          this.notifications.set(
            Array.isArray(rows)
              ? rows
                  .map((row) => this.normalize(row))
                  .filter((row): row is UserNotification => row !== null)
              : [],
          );
          this.failed.set(false);
        },
        error: () => this.failed.set(true),
      });
  }

  markRead(id: number): void {
    if (!Number.isSafeInteger(id) || id <= 0) return;
    this.client.patch<unknown>(`user-notification/${id}/read`, {}).subscribe({
      next: () =>
        this.notifications.update((items) =>
          items.map((item) =>
            item.id === id ? { ...item, status: 'read' } : item,
          ),
        ),
      error: () => this.failed.set(true),
    });
  }

  dismiss(id: number): void {
    if (!Number.isSafeInteger(id) || id <= 0) return;
    this.client
      .patch<unknown>(`user-notification/${id}/dismiss`, {})
      .subscribe({
        next: () =>
          this.notifications.update((items) =>
            items.filter((item) => item.id !== id),
          ),
        error: () => this.failed.set(true),
      });
  }

  private record(value: unknown): Record<string, unknown> {
    return value !== null && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : {};
  }

  private normalize(value: unknown): UserNotification | null {
    const row = this.record(value);
    const id = Number(row['id'] ?? row['Id']);
    const title = row['title'] ?? row['Title'];
    const message = row['message'] ?? row['Message'];
    const status = row['status'] ?? row['Status'];
    if (
      !Number.isSafeInteger(id) ||
      id <= 0 ||
      typeof title !== 'string' ||
      typeof message !== 'string'
    )
      return null;
    return {
      id,
      title,
      message,
      type: String(row['type'] ?? row['Type'] ?? ''),
      status: status === 'unread' || status === 0 ? 'unread' : 'read',
      createdAt: String(row['createdAt'] ?? row['CreatedAt'] ?? ''),
    };
  }
}
